import { Cents, DateTimeString } from '@betterment/js-runtime';
import { GoalIconProps } from '@betterment/style-closet-react';

import { TransactionCategory } from './components/TransactionIcon';

import { RestThemedAsset } from '../Shared/data';
import { FilterParams as DateFilterParams } from '../Shared/date-filter';

type ThemedAsset = {
  lightUrl: string;
  darkUrl: string;
};

type CheckingEligibilityStatus =
  | 'eligible'
  | 'suspended'
  | 'manual_review'
  | 'account_group_locked'
  | 'invalid_dpo_address'
  | 'suspended_documents_required';

export interface Address {
  addressLine1: string;
  addressLine2?: string | null;
  city: string;
  state: string;
  zip: string;
}

export interface User {
  id: number;
  checkingEligibilityStatus: CheckingEligibilityStatus;
  isB4BOnly: boolean;
  isCheckingOnly: boolean;
  requiresOccupation: boolean;
  fullName: string;
  residentialAddress: Address;
  mailingAddress?: Address;
}

export enum UserSearchStatus {
  NotFound = 'not_found',
  Found = 'found',
  LimitReached = 'limit_reached',
  InvitedSelf = 'invited_self',
  ProspectSecondary = 'prospect_secondary',
}

export type UserSearch =
  | { searchStatus: UserSearchStatus.Found; searchResult: UserMatch }
  | { searchStatus: UserSearchStatus.NotFound }
  | { searchStatus: UserSearchStatus.LimitReached }
  | { searchStatus: UserSearchStatus.InvitedSelf };

export interface UserMatch {
  userId: number;
  email: string;
  firstName: string;
  lastName: string;
}

export interface AccountCreationRequestResponse {
  requestId: string;
  defaultAccountName: string;
}

export interface AccountCreationRequestCompletionResponse {
  accountId: Account['id'];
}

export interface JointInvitationRequestResponse {
  searchResult: UserMatch | null;
  searchStatus: UserSearchStatus;
  requestId: string;
}

export interface JointInvitationRequestParams {
  email: string;
  socialSecurityNumber: string;
}

export interface ProspectJointInvitationRequestEligibleResponse {
  isEligible: true;
  requestId: string;
}

export interface ProspectJointInvitationRequestIneligibleResponse {
  isEligible: false;
  ineligibilityMessageTitle: string;
  ineligibilityMessageDescription: string;
}

export type ProspectJointInvitationRequestResponse =
  | ProspectJointInvitationRequestEligibleResponse
  | ProspectJointInvitationRequestIneligibleResponse;

export interface ProspectJointInvitationRequestParams {
  email: string;
  socialSecurityNumber: string;
  firstName: string;
  middleName: string | undefined;
  lastName: string;
  dateOfBirth: string;
  phoneNumber: string;
  residentialAddress: Address;
}

export interface CustomerAgreements {
  bankAgreements: boolean;
  retailAgreements: boolean;
}

export interface CustomerCreateParams extends AddressParams, CustomerAgreements {
  invitationId?: string | undefined;
}

export enum PrimaryIdStatus {
  Present = 'present',
  NotPresent = 'not_present',
  NotApplicable = 'not_applicable',
}

export interface CustomerCreateResponse {
  primaryIdStatus: PrimaryIdStatus;
  nextStep: 'retry' | 'success' | 'manual_review' | 'limit_error';
  invitationId: string | undefined;
}

type BankDocumentType =
  | 'TermsAndConditions'
  | 'PrivacyPolicy'
  | 'TruthInSavings'
  | 'eStatement'
  | 'ConsumerDepositAgreement'
  | 'FeeSchedule'
  | 'ReferAFriend'
  | 'EftInfo'
  | 'ProductInfo'
  | 'CardInfo'
  | 'FeeDisclosure';

export interface BankDocument {
  id: number;
  title: string;
  documentType: BankDocumentType;
}

export interface Account {
  id: number;
  name: string;
  createdAt: DateTimeString;
  accountNumber: string;
  accountNumberMasked: string;
  routingNumber: string;
  routingNumberMasked: string;
  type: string;
  status: string;
  accountBalanceCents: Cents;
  availableBalanceCents: Cents;
  pendingBalanceCents: Cents;
  cumulativeReimbursedAtmFeesCents: Cents;
  cumulativeEarnedCashbackRewardsCents: Cents;
  isJointAccount: boolean;
  isSpendingAccount: boolean;
  jointUsersNames?: UserName[];
  sentInvitations: Invitation[];
  isLocked: boolean;
  icon: ThemedAsset;
}

export interface UserName {
  firstName: string;
  lastName: string;
}

export interface Invitation {
  invitationId: string;
  senderFirstName: string;
  senderLastName: string;
  receiverFirstName: string;
  receiverLastName: string;
  accountId: number;
  expiresAt: DateTimeString;
  status: InvitationStatus;
  isSenderStatusValid: boolean;
}

export enum InvitationStatus {
  AccountCreated = 'account_created',
  InviteSent = 'invite_sent',
  Complete = 'complete',
  AccountClosed = 'account_closed',
  Expired = 'expired',
  Declined = 'declined',
  Canceled = 'canceled',
}

export enum CardStatus {
  Initiated = 'Initiated',
  Pending = 'Pending',
  PendingVerification = 'PendingVerification',
  Verified = 'Verified',
  Denied = 'Denied',
  Expired = 'Expired',
  Archived = 'Archived',
  Reissued = 'Reissued',
  Hotlisted = 'Hotlisted',
  ReissuedPendingVerification = 'ReissuedPendingVerification',
  AutoReissuedPendingVerification = 'AutoReissuedPendingVerification',
}

export enum LockTypeCode {
  Unlocked = 'UNL',
  CustomerLocked = 'CST',
  SystemLocked = 'SYS',
}

export enum LockReasonTypeCode {
  Lost = 'LST',
  Stolen = 'STL',
  Fraud = 'FRD',
  Temporary = 'TMP',
  Unknown = 'UNK',
}

export interface Card {
  id: number;
  status: CardStatus;
  lockTypeCode: LockTypeCode;
  lockReasonTypeCode: LockReasonTypeCode;
  issuedAt: DateTimeString;
  expectedDeliveryAt: DateTimeString;
  spendingAccountId: number;
  hasActiveSpendingAccountChangeRequest: boolean;
}

export interface ExternalAccount {
  id: number;
  displayName: string;
  balanceCents: Cents;
  balanceUpdatedAt: DateTimeString;
  isLocked: boolean;
  isBettermentChecking: boolean;
  icon: RestThemedAsset;
}

export enum CustomerStatus {
  Initiated = 'initiated',
  ManualReview = 'manual_review',
  Verified = 'verified',
  Denied = 'denied',
  Archived = 'archived',
  Expired = 'expired',
}

export interface Customer {
  account: Account;
  accounts: Account[];
  card: Card;
  cardPendingVerification: Card | null;
  externalAccount?: ExternalAccount;
  isLocked: boolean;
  isSuspended: boolean;
  customerStatus: CustomerStatus;
  isCashbackRewardsEligible: boolean;
}

export enum TransactionStatus {
  Pending = 'Pending',
  Settled = 'Settled',
  Voided = 'Voided',
}

export interface TransactionType {
  code: TransactionTypeCode | CheckDepositTransactionTypeCode | CheckWithdrawalTransactionTypeCode;
  description: string;
}

export enum CheckDepositTransactionTypeCode {
  CHKATH = 'CHKATH',
  CHKDEP = 'CHKDEP',
  CHKDUP = 'CHKDUP',
  CHKDDN = 'CHKDDN',
  CHKDRT = 'CHKDRT',
}

export enum CheckWithdrawalTransactionTypeCode {
  CHKWAT = 'CHKWAT',
  CHKWID = 'CHKWID',
}

export enum TransactionTypeCode {
  CHKRUP = 'CHKRUP',
  CHKRDN = 'CHKRDN',
  ATHAFD = 'ATHAFD',
  ATHCSH = 'ATHCSH',
  ATHDBT = 'ATHDBT',
  ATHPCH = 'ATHPCH',
  CPDEP = 'CPDEP',
  UNKDEP = 'UNKDEP',
  RTNDEP = 'RTNDEP',
  VERDEP = 'VERDEP',
  CPWTH = 'CPWTH',
  UNKWTH = 'UNKWTH',
  RTNWTH = 'RTNWTH',
  VERWTH = 'VERWTH',
  CRDAFD = 'CRDAFD',
  CRDPCH = 'CRDPCH',
  REVAFD = 'REVAFD',
  REVPCH = 'REVPCH',
  CRDCSH = 'CRDCSH',
  CRDICF = 'CRDICF',
  REVCSH = 'REVCSH',
  CRDRTN = 'CRDRTN',
  REVRTN = 'REVRTN',
  CPXFR = 'CPXFR',
}

export interface TransactionCancelability {
  eligible: boolean;
  expired: boolean;
  deadline: DateTimeString;
}

export interface DetailedTransaction {
  id: number | string;
  origin?: string;
  rawDescription?: string;
  formattedDescription: string;
  status: TransactionStatus;
  createdAt: DateTimeString;
  availableAt: DateTimeString;
  amountCents: Cents | null;
  earnedCashbackRewardsCents: Cents;
  pendingCashbackRewardsCents: Cents;
  cashbackRewardSourceTransactions?: DetailedTransaction[];
  isCredit: boolean;
  type: TransactionType;
  bettermentCategory?: TransactionCategory;
  category?: TransactionCategory;
  merchant?: TransactionMerchant;
  cancelability: TransactionCancelability;
  check?: Check;
  depositAvailability?: TransactionDepositAvailability;
  icon?: ThemedAsset;
  isDirty?: boolean;
}

export interface TransactionMerchant {
  name: string;
  iconUrl: string;
}

export interface TransactionGoalInfo {
  id: number;
  coverImage: {
    type: 'custom' | 'standard';
    name: GoalIconProps['name'] | null;
  };
  purposeIcon: ThemedAsset;
}

export interface TransactionDepositAvailability {
  totalReleasedAmountCents: Cents;
  totalRemainingAmountCents: Cents;
  amounts: DepositAvailabilityAmount[];
}

export interface DepositAvailabilityAmount {
  effectiveAvailabilityDate: DateTimeString;
  releaseAmountCents: Cents;
  remainingAmountCents: Cents;
  isReleased: boolean;
}

export interface Check {
  number: number;
  amountCents: Cents;
  returnReason: string | null;
  images: CheckImage[];
}

export enum CheckImageType {
  CheckFront = 'CheckFront',
  CheckBack = 'CheckBack',
  ReturnCheckFront = 'ReturnCheckFront',
  ReturnCheckBack = 'ReturnCheckBack',
}

export interface CheckImage {
  type: CheckImageType;
  url: string;
}

export interface FormattedTransactionsFilterParams {
  page?: number;
  startOn?: string;
  endOn?: string;
  startDate?: string;
  endDate?: string;
  categories?: string[];
}

export interface UnformattedTransactionsFilterParams extends DateFilterParams {
  page?: number;
  categories?: string[];
}

export interface ActivateCardParams {
  dateOfBirth: Date;
  cardNumberMasked: string;
}

export interface PinResetParams {
  newPin: string;
}

export interface AddressParams {
  residentialAddress: Address;
  mailingAddress: Address;
}

export interface AddressValidationParams extends Partial<AddressParams> {
  isResidential: boolean;
}

export interface DepositParams {
  externalAccountId: number;
  amountCents: Cents;
}

export interface WithdrawalParams {
  externalAccountId: ExternalAccount['id'];
  amountCents: Cents;
}

export interface TransferAvailabilityEstimate {
  estimatedDate: DateTimeString;
}

export interface AccountTask<T extends string = string> {
  name: T;
  isSkippable: boolean;
}

export interface StateAgeLimitEligibility {
  isEligible: boolean;
  ineligibilityMessage?: { title: string; description: string };
}
